import { FormEvent, useState } from 'react';
import { useResetPasswordStatusQuery, useResetPasswordMutation } from 'app/services';
import { errorDetail } from 'helpers/apiError';
import { useBodyClass } from 'hooks/useBodyClass';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { track } from 'app/analytics';

// Faithful re-implementation of reset_password.html. The emailed
// /reset-password/<token>/ link is validated by Django, which stashes the token
// in the session and redirects here; we read that via the status endpoint.
const ResetPassword = () => {
  const { data: status, isLoading: checking } = useResetPasswordStatusQuery();
  const [resetPassword] = useResetPasswordMutation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  useBodyClass('sign-in');
  useDocumentTitle('Kinetix - Reset Password');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await resetPassword({ new_password: newPassword, confirm_password: confirmPassword }).unwrap();
      track('password_reset_completed');
      setDone(true);
    } catch (err) {
      setError(errorDetail(err, 'Could not reset your password.'));
    }
  };

  const shell = (body: React.ReactNode) => (
    <div className="sign-in-container">
      <div className="login">
        <div className="login-logo" />
        <div className="form-info">
          <h4 className="form-name">Reset Password</h4>
        </div>
        {body}
      </div>
    </div>
  );

  if (checking) return shell(<p style={{ lineHeight: 1.6 }}>Loading…</p>);

  if (!status?.valid) {
    return shell(
      <>
        <p style={{ lineHeight: 1.6 }}>
          This password reset link is invalid or has expired. Reset links are valid for 1 hour and
          can only be used once.
        </p>
        <div className="button">
          <a href="/forgot-password/" style={{ display: 'inline-block', textAlign: 'center', textDecoration: 'none' }}>
            Request a new link
          </a>
        </div>
      </>
    );
  }

  if (done) {
    return shell(
      <>
        <p style={{ lineHeight: 1.6 }}>
          Your password has been updated. You can now log in with your new password.
        </p>
        <div className="button">
          <a href="/login/" style={{ display: 'inline-block', textAlign: 'center', textDecoration: 'none' }}>
            Go to Login
          </a>
        </div>
      </>
    );
  }

  return shell(
    <form id="reset-password-form" method="post" action="/reset-password/" onSubmit={onSubmit}>
      <p style={{ lineHeight: 1.6, marginBottom: 16 }}>
        Setting a new password for <strong>{status.username}</strong>.
      </p>
      {error && (
        <div className="form-error" style={{ display: 'block' }}>
          <p className="error-item">{error}</p>
        </div>
      )}
      <div className="second-input position-relative">
        <input
          id="new_password"
          type="password"
          name="new_password"
          className="form-field"
          placeholder="New Password (case sensitive)"
          required
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <div className="password-input-btm">
          <div className="password-conditions">
            <span>Password conditions</span>
            <ul className="passowrd-tooltip">
              <li>{`Password characters must be alphanumeric + !@#$%^&*()-_=+[]{}|;:'",.<>?/\\`}</li>
              <li>Password must be 7 up to 55 characters.</li>
              <li>Password is case sensitive.</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="second-input position-relative">
        <input
          id="confirm_password"
          type="password"
          name="confirm_password"
          className="form-field"
          placeholder="Confirm New Password"
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </div>
      <div className="button">
        <input type="submit" value="Update Password" />
      </div>
    </form>
  );
};

export default ResetPassword;
